import { Capacitor } from '@capacitor/core'
import { AppLauncher } from '@capacitor/app-launcher'
import { Directory, Filesystem } from '@capacitor/filesystem'
import { Share } from '@capacitor/share'

const sanitizeFileName = (fileName: string) =>
  fileName.replace(/[<>:"/\\|?*\u0000-\u001f]/g, '-').replace(/\s+/g, ' ').trim() || 'download'

const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(reader.error || new Error('The file could not be read.'))
    reader.onload = () => {
      const result = String(reader.result || '')
      resolve(result.includes(',') ? result.slice(result.indexOf(',') + 1) : result)
    }
    reader.readAsDataURL(blob)
  })

export const fileNameFromContentDisposition = (header?: string, fallback = 'download') => {
  if (!header) return fallback

  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i)
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].replace(/["']/g, ''))
    } catch {
      return utf8Match[1].replace(/["']/g, '')
    }
  }

  return header.match(/filename="?([^";]+)"?/i)?.[1]?.trim() || fallback
}

export const saveOrShareBlob = async (blob: Blob, requestedFileName: string): Promise<void> => {
  const fileName = sanitizeFileName(requestedFileName)

  if (Capacitor.isNativePlatform()) {
    const data = await blobToBase64(blob)
    const result = await Filesystem.writeFile({
      path: `downloads/${Date.now()}-${fileName}`,
      data,
      directory: Directory.Cache,
      recursive: true
    })

    await Share.share({
      title: fileName,
      text: 'Choose Print, Drive, WhatsApp, Files, or another app',
      url: result.uri,
      dialogTitle: `Print, save, or share ${fileName}`
    })
    return
  }

  const objectUrl = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = objectUrl
  link.download = fileName
  link.click()
  URL.revokeObjectURL(objectUrl)
}

/** Shares a picture together with its caption so WhatsApp opens with both ready to send. */
export const shareImageWithText = async (image: Blob, fileName: string, title: string, text: string): Promise<boolean> => {
  const safeName = sanitizeFileName(fileName)

  if (Capacitor.isNativePlatform()) {
    const written = await Filesystem.writeFile({
      path: `notices/${Date.now()}-${safeName}`,
      data: await blobToBase64(image),
      directory: Directory.Cache,
      recursive: true
    })
    await Share.share({ title, text, files: [written.uri], dialogTitle: 'Send this notice in WhatsApp' })
    return true
  }

  const file = new File([image], safeName, { type: image.type || 'image/jpeg' })
  const nav = navigator as Navigator & { canShare?: (data: ShareData) => boolean }
  if (nav.share && nav.canShare?.({ files: [file] })) {
    await nav.share({ files: [file], text, title })
    return true
  }
  return false
}

export const sharePlainText = async (title: string, text: string) => {
  if (!text.trim()) {
    return
  }

  if (Capacitor.isNativePlatform()) {
    await Share.share({
      title,
      text,
      dialogTitle: 'Open in ChatGPT, Gemini, Claude, or another app'
    })
    return
  }

  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    await navigator.share({ title, text })
    return
  }

  await navigator.clipboard.writeText(text)
}

const opensMessagingAppDirectly = (url: string) =>
  /^(https?:\/\/(wa\.me|api\.whatsapp\.com|chat\.whatsapp\.com)|tel:)/i.test(url)

const messageFromWhatsAppUrl = (url: string) => {
  const text = url.match(/[?&]text=([^&]*)/i)?.[1]
  if (!text) return url
  try {
    return decodeURIComponent(text.replace(/\+/g, ' '))
  } catch {
    return url
  }
}

const tryLaunch = async (url: string) => {
  try {
    const result = await AppLauncher.openUrl({ url })
    return result?.completed !== false
  } catch {
    return false
  }
}

/** Opens a WhatsApp chat. Returns true when a WhatsApp app (or the share sheet fallback) was launched. */
export const openWhatsAppUrl = async (url: string, preOpenedWindow?: Window | null): Promise<boolean> => {
  if (!url) {
    return false
  }

  if (Capacitor.isNativePlatform()) {
    const waMe = url.match(/^https?:\/\/wa\.me\/([^?]+)(?:\?(.*))?$/i)
    const nativeUrl = waMe
      ? `whatsapp://send?phone=${encodeURIComponent(waMe[1])}${waMe[2] ? `&${waMe[2]}` : ''}`
      : url.replace(/^https?:\/\/api\.whatsapp\.com\/send/i, 'whatsapp://send')

    // 1) WhatsApp directly, 2) the https link (Android routes it to WhatsApp or WhatsApp Business), 3) share the message text.
    if (await tryLaunch(nativeUrl)) return true
    if (/^https?:/i.test(url) && (await tryLaunch(url))) return true
    try {
      await Share.share({ title: 'WhatsApp message', text: messageFromWhatsAppUrl(url), dialogTitle: 'Choose an app to continue' })
      return true
    } catch {
      return false
    }
  }

  const popup = preOpenedWindow ?? window.open('about:blank', '_blank')
  if (popup) {
    popup.location.href = url
    return true
  }

  window.location.assign(url)
  return true
}

export const openExternalUrl = async (
  url: string,
  title = 'Open link',
  preOpenedWindow?: Window | null
): Promise<boolean> => {
  if (!url) {
    return false
  }

  if (opensMessagingAppDirectly(url)) {
    return openWhatsAppUrl(url, preOpenedWindow)
  }

  if (Capacitor.isNativePlatform()) {
    await Share.share({
      title,
      url,
      dialogTitle: title
    })
    return true
  }

  window.open(url, '_blank', 'noopener,noreferrer')
  return true
}

